"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import Image, { type StaticImageData } from "next/image";
import { useParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import lineIcon from "@/assets/images/account/line-icon.png";
import appleIcon from "@/assets/images/common/apple-icon.png";
import googleIcon from "@/assets/images/common/google-icon.png";
import CfInfoDialog from "@/components/pop-ui/cf-info-dialog";
import { toast } from "@/components/pop-ui/pop-toast";
import { useCartMerge } from "@/hooks/use-cart-merge";
import { useGlobalConfig } from "@/providers/global-config-context";
import { useTurnstile } from "@/providers/turnstile-provider";
import { authClient } from "../../../lib/auth/auth-client";
import { getAuthHeadersWaitSign } from "../../../lib/auth/td-utils";
interface LoginSocialButtonProps {
  area: string;
  onError?: (error: any) => void;
  disabled?: boolean;
  onDisabledClick?: () => void;
  onSuccess?: () => void;
}
interface LoginSocialPopupProps extends LoginSocialButtonProps {
  provider: "apple" | "google";
  icon: StaticImageData;
}
function LoginSocialPopup({
  provider,
  icon,
  onError,
  disabled = false,
  onDisabledClick,
  onSuccess
}: LoginSocialPopupProps) {
  const {
    t
  } = useTranslation();
  const params = useParams();
  const popupRef = useRef<Window | null>(null);
  const {
    globalConfig
  } = useGlobalConfig();
  const {
    isSdkReady,
    sdkError
  } = useTurnstile();
  const isTurnstileEnabled = globalConfig?.PUBLIC_TURNSTILE_ENABLED;
  const {
    handleLoginSuccess: handleCartMerge
  } = useCartMerge();
  const {
    refetch: refetchSession
  } = authClient.useSession();

  // Dialog open state for captcha (closed by default, opens when login is attempted)
  const [captchaOpen, setCaptchaOpen] = useState(false);
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === "THIRD_PARTY_LOGIN_SUCCESS" && event.data.provider === provider) {
        popupRef.current = null;
        // 刷新 session 并合并购物车
        await refetchSession();
        await handleCartMerge();
        onSuccess?.();
      } else if (event.data.type === "THIRD_PARTY_LOGIN_ERROR" && event.data.provider === provider) {
        popupRef.current = null;
        onError?.(event.data.error);
      } else if (event.data.type === "THIRD_PARTY_LOGIN_CANCELLED" && event.data.provider === provider) {
        popupRef.current = null;
      }
    };
    const checkWindowClosed = () => {
      if (popupRef.current?.closed) {
        popupRef.current = null;
      }
    };
    window.addEventListener("message", handleMessage);
    const intervalId = setInterval(checkWindowClosed, 1000);
    return () => {
      window.removeEventListener("message", handleMessage);
      clearInterval(intervalId);
    };
  }, [onSuccess, onError, provider, refetchSession, handleCartMerge]);

  // Handle Turnstile completion - proceed with social login after verification
  const handleTurnstileSuccess = (token: string) => {
    setCaptchaOpen(false);
    // After Turnstile verification on Safari, avoid popup blockers by doing a top-level redirect
    try {
      const url = new URL(`${window.location.origin}/${params.locale}/thirdMd/${provider}`);
      if (token) {
        url.searchParams.set("cf-turnstile-response", token);
      }
      window.location.href = url.toString();
    } catch {
      // Fallback to popup if navigation fails for any reason
      signIn(token);
    }
  };
  const {
    mutate: signIn,
    isPending
  } = useMutation({
    mutationFn: async (turnstileToken?: string) => {
      // Build the URL with Turnstile token if provided
      const url_0 = new URL(`${window.location.origin}/${params.locale}/thirdMd/${provider}`);
      if (turnstileToken) {
        url_0.searchParams.set("cf-turnstile-response", turnstileToken);
      }
      const popup = window.open(url_0.toString());
      if (!popup) {
        throw new Error("弹窗被阻止，请允许弹窗后重试");
      }
      popupRef.current = popup;
    },
    onError: (error: any) => {
      console.error(`${provider} 登录失败`, error);
      popupRef.current = null;
      if (onError) onError(error);
    }
  });
  const handleSignIn = () => {
    if (disabled) {
      onDisabledClick?.();
      return;
    }
    // If Turnstile is enabled, check SDK readiness before opening dialog
    if (isTurnstileEnabled) {
      if (sdkError) {
        // SDK failed to load, show error and don't open dialog
        toast.error(t("gateway.error.475"));
        return;
      }
      if (!isSdkReady) {
        // SDK is still loading, do nothing (user can try again)
        return;
      }
      // SDK is ready, open Turnstile dialog
      setCaptchaOpen(true);
      return;
    }

    // Turnstile disabled, proceed with normal social login
    signIn(undefined);
  };
  const ariaLabelKey = `ariaLabel.signInWith${provider.charAt(0).toUpperCase() + provider.slice(1)}`;
  const altKey = `alt.${provider}Icon`;
  return <>
			<button onClick={handleSignIn} aria-label={t(ariaLabelKey)} disabled={isPending} type="button" className="rounded-full">
				<Image src={icon} alt={t(altKey)} className="size-10" width={40} height={40} unoptimized />
			</button>

			<CfInfoDialog open={captchaOpen} onOpenChange={setCaptchaOpen} containerId={`cf-turnstile-container-${provider}-login-dialog`} onTurnstileSuccess={handleTurnstileSuccess} onTurnstileError={() => toast.error(t("gateway.error.476"))} />
		</>;
}
export function LoginSocialApple(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <LoginSocialPopup {...props} provider="apple" icon={appleIcon} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function LoginSocialGoogle(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <LoginSocialPopup {...props} provider="google" icon={googleIcon} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
interface LoginSocialLineProps {
  area: string;
  onError?: (error: any) => void;
  disabled?: boolean;
  onDisabledClick?: () => void;
}
export function LoginSocialLine(t0) {
  const $ = _c(37);
  const {
    onError,
    disabled,
    onDisabledClick
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    globalConfig
  } = useGlobalConfig();
  const {
    isSdkReady,
    sdkError
  } = useTurnstile();
  const isTurnstileEnabled = globalConfig?.PUBLIC_TURNSTILE_ENABLED;
  const {
    handleLoginSuccess: handleCartMerge
  } = useCartMerge();
  const {
    refetch: refetchSession
  } = authClient.useSession();
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const handleTurnstileSuccess = _token => {
    setCaptchaOpen(false);
    lineSignIn();
  };
  let t1;
  if ($[0] !== handleCartMerge || $[1] !== refetchSession) {
    t1 = async () => {
      await refetchSession();
      await handleCartMerge();
    };
    $[0] = handleCartMerge;
    $[1] = refetchSession;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onError) {
    t2 = error => {
      console.error("Line \u767B\u5F55\u5931\u8D25", error);
      if (onError) {
        onError(error);
      }
    };
    $[3] = onError;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: _temp,
      onSuccess: t1,
      onError: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    mutate: t4,
    isPending: isLineSigningIn
  } = useMutation(t3);
  const lineSignIn = t4;
  let t5;
  if ($[8] !== disabled || $[9] !== isSdkReady || $[10] !== isTurnstileEnabled || $[11] !== lineSignIn || $[12] !== onDisabledClick || $[13] !== sdkError || $[14] !== t) {
    t5 = () => {
      if (disabled) {
        onDisabledClick?.();
        return;
      }
      if (isTurnstileEnabled) {
        if (sdkError) {
          toast.error(t("gateway.error.475"));
          return;
        }
        if (!isSdkReady) {
          return;
        }
        setCaptchaOpen(true);
        return;
      }
      lineSignIn();
    };
    $[8] = disabled;
    $[9] = isSdkReady;
    $[10] = isTurnstileEnabled;
    $[11] = lineSignIn;
    $[12] = onDisabledClick;
    $[13] = sdkError;
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleLineSignIn = t5;
  let t6;
  if ($[16] !== t) {
    t6 = t("ariaLabel.signInWithLine");
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = isLineSigningIn || disabled;
  const t8 = `rounded-full ${disabled ? "cursor-not-allowed" : ""}`;
  let t9;
  if ($[18] !== t) {
    t9 = t("alt.lineIcon");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <Image src={lineIcon} alt={t9} className="size-10" width={40} height={40} unoptimized={true} />;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleLineSignIn || $[23] !== t10 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8) {
    t11 = <button onClick={handleLineSignIn} aria-label={t6} disabled={t7} type="button" className={t8}>{t10}</button>;
    $[22] = handleLineSignIn;
    $[23] = t10;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = () => toast.error(t("gateway.error.476"));
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== captchaOpen || $[31] !== handleTurnstileSuccess || $[32] !== t12) {
    t13 = <CfInfoDialog open={captchaOpen} onOpenChange={setCaptchaOpen} containerId="cf-turnstile-container-line-login-dialog" onTurnstileSuccess={handleTurnstileSuccess} onTurnstileError={t12} />;
    $[30] = captchaOpen;
    $[31] = handleTurnstileSuccess;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t11 || $[35] !== t13) {
    t14 = <>{t11}{t13}</>;
    $[34] = t11;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}
async function _temp() {
  const requestHeaders = await getAuthHeadersWaitSign("line-oauth");
  return authClient.signIn.oauth2({
    providerId: "line",
    callbackURL: "/account",
    fetchOptions: {
      credentials: "include",
      headers: requestHeaders
    }
  });
}